import { Collapse, Form, Select, Spin } from "antd";
import Axios from "axios";
import { useCallback, useRef, useState } from "react";
import CustomCollapse from "../../../components/customCollapse";
import { renderErrorMessage } from "../../../components/messages/errorMessage";
import _t from "../../../lang/translate";
import { findMotorAtlasEntries } from "../../../services/motorAtlasService";
import { ModelCatalogFilterForm, ModelMatch, SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";
import useMotorAtlasOptions from "./useMotorAtlasOptions";
import { clearDependentFields, ModelMatcherInputVehicle } from "./util";

type FiltersProps = {
  setMatches: React.Dispatch<React.SetStateAction<ModelMatch[] | null>>
  inputVehicle: ModelMatcherInputVehicle | null,
  sourceVehicle: SourceVehiclePreview | null,
}

export default function MotorAtlasExpandedSearch({ setMatches, inputVehicle, sourceVehicle }: FiltersProps) {
  const reqIdRef = useRef(0)
  const [form] = Form.useForm<ModelCatalogFilterForm>()
  const [loading, setLoading] = useState(false)
  const {
    handleDrillDown,
    loadingOptionsFields,
    options,
  } = useMotorAtlasOptions("select", true)

  const handleFetch = useCallback(async (values: ModelCatalogFilterForm) => {
    reqIdRef.current += 1;
    const requestId = reqIdRef.current;
    try {
      setLoading(true)
      const res = await findMotorAtlasEntries(values, inputVehicle?.firstRegDate, false, sourceVehicle?.supplementary.potentialECodes ?? [])
      setMatches(res.data.data)
    } catch (error) {
      if (!Axios.isCancel(error)) {
        renderErrorMessage(error)
      }
    } finally {
      // Only the latest request can flip loading state back
      if (reqIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [])

  function applyFilterPipeline(rawValues: ModelCatalogFilterForm, changedKey?: keyof ModelCatalogFilterForm) {
    const cleaned = clearDependentFields(rawValues, changedKey, undefined)
    form.setFieldsValue(cleaned);
    handleDrillDown(cleaned, changedKey)
    if (changedKey === 'subModel') {
      handleFetch(cleaned)
    } else {
      setMatches([]);
    }
  }

  function handleValuesChange(changed: Partial<ModelCatalogFilterForm>, all: ModelCatalogFilterForm) {
    const key = Object.keys(changed)[0] as keyof ModelCatalogFilterForm
    applyFilterPipeline(all, key);
  }

  return (
    <Form
      form={form}
      labelAlign="left"
      labelCol={{ flex: "0 0 80px" }}
      className="motor_atlas_catalog_mapper_form position-relative"
      size="middle"
      colon={false}
      onValuesChange={handleValuesChange}
    >
      <CustomCollapse defaultActiveKey={["1"]} className="ml-0 mr-0">
        <Collapse.Panel forceRender key={"1"} header={
          <div>
            <Spin style={{ position: "absolute", top: 8, right: 17 }} size="default" spinning={loading} />
            <div>
              <span style={{ fontSize: 14 }}>{_t("filters")}</span>
            </div>
          </div>
        }
        >
          <Form.Item name={"type"} label={_t("type")}>
            <Select allowClear options={options.type} />
          </Form.Item>
          <Form.Item label={_t("brand")} name={['make']}>
            <Select loading={loadingOptionsFields.has("make")}
              disabled={!options.make?.length}
              showSearch
              placeholder={_t("choose")}
              optionFilterProp="label"
              dropdownMatchSelectWidth={400}
              allowClear
              options={options.make} />
          </Form.Item>

          <Form.Item label={_t("model")} name={['model']}>
            <Select loading={loadingOptionsFields.has("model")}
              disabled={!options.model?.length}
              showSearch
              placeholder={_t("choose")}
              optionFilterProp="label"
              dropdownMatchSelectWidth={400}
              allowClear
              options={options.model} />
          </Form.Item>
          <Form.Item label={_t("subModel")} name={['subModel']}>
            <Select loading={loadingOptionsFields.has("subModel")}
              disabled={!options.subModel?.length}
              showSearch
              placeholder={_t("choose")}
              optionFilterProp="label"
              dropdownMatchSelectWidth={400}
              allowClear
              options={options.subModel} />
          </Form.Item>
        </Collapse.Panel>
      </CustomCollapse>
    </Form>
  )
}
